<!--
  navItem "Verträge / Haftungen" — a two-pane REVIEW WORKBENCH (skill fe_core_review-workbench).

  LEFT  (#workbenchList, persistent): the filters (Provisionsart, Haftungsstatus, Zurücksetzen), the
        selection line, the QL table of the Provisionssätze and the action entries — the three
        existing dialogs of 04-frontend.md §7 PLUS the two additive top-down wizards (user decision
        O-4: the three dialogs stay, unchanged, next to them).
  RIGHT (#workbenchDetail): the selected Provisionssatz in object mode. The pane is ALWAYS mounted,
        only its CONTENT is conditional (R3), and the 1<->2 toggle runs through [panes] on the SAME
        splitter (R2) — an @if around the workbench would rebuild the splitter and remount the
        projected table, losing sort order, filters, scroll position and selection on every click.

  FD-4 — this is a LEVEL-2 workbench whenever the OBC was opened from /#/cp/dashboard/agents. It
  therefore binds NEITHER [autoFitViewport] NOR [viewportOffsetPx] and resolves to height:100% of the
  height chain documented in cp-provision-agent-object.component.scss. That is deliberate, not an
  omission: auto-fit has no container awareness and would size a nested instance to the window bottom.

  AUTHORING ORDER (FD-4): @ContentChild defaults to descendants:true, so #workbenchList and
  #workbenchDetail are declared here, at the top level of this workbench, BEFORE anything that could
  itself project a template.

  Every write still happens in a metadata-driven dialog at the end of this file — no form HTML here.
-->
<div class="cp-provision-agent-contract-agents">

    @if (!initialized) {

        <p-skeleton height="18rem"></p-skeleton>

    } @else if (!provisionAgentId) {

        <p-message severity="info" text="Kein Abrechnungskonto ausgewählt." styleClass="w-full"></p-message>

    } @else {

        <mvs-review-workbench
                class="cp-contract-agents-workbench"
                [panes]="allowDetailPane && selectedProvisionContractAgentId ? 2 : 1"
                [panelSizes]="[58, 42]"
                [minSizes]="[30, 25]">

            <!-- LEFT: the worklist. Persistent — exactly one instantiation site. -->
            <ng-template #workbenchList>
                <div class="cp-contract-agents-pane pr-3">

                    <!-- While a cp job is running, the amounts and the liability state are an interim state -->
                    @if (jobRunning) {
                        <div class="flex align-items-center gap-2 mb-3 p-3 border-round-xl bg-orange-100 text-orange-800">
                            <i class="fa-solid fa-spinner fa-spin"></i>
                            <span class="text-sm">
                                Hintergrundprozesse laufen – die Provisionssätze werden nach Abschluss automatisch
                                aktualisiert.
                            </span>
                        </div>
                    }

                    <!-- Filter (04 §7: Provisionsart + Haftungsstatus) and the action entries -->
                    <div class="surface-card border-round p-3 mb-3">
                        <div class="flex flex-wrap align-items-end gap-3">

                            <div class="flex flex-column gap-1">
                                <label class="text-color-secondary text-sm">Provisionsart</label>
                                <p-select
                                        [options]="provisionTypeComponentOptions"
                                        [(ngModel)]="filterProvisionTypeComponentId"
                                        optionLabel="label"
                                        optionValue="value"
                                        styleClass="w-16rem"
                                        (onChange)="handleFilterChange()">
                                </p-select>
                            </div>

                            <div class="flex flex-column gap-1">
                                <label class="text-color-secondary text-sm">Haftungsstatus</label>
                                <p-select
                                        [options]="liabilityStatusOptions"
                                        [(ngModel)]="filterLiabilityStatus"
                                        optionLabel="label"
                                        optionValue="value"
                                        styleClass="w-14rem"
                                        (onChange)="handleFilterChange()">
                                </p-select>
                            </div>

                            <p-button
                                    label="Filter zurücksetzen"
                                    icon="fa-regular fa-filter-circle-xmark"
                                    severity="secondary"
                                    [text]="true"
                                    (onClick)="handleFilterReset()">
                            </p-button>

                        </div>

                        <!-- The three existing §7 dialogs and the two additive wizards, in one row.
                             O-4: nothing here replaces anything — the wizards come ON TOP. -->
                        <div class="flex flex-wrap gap-2 mt-3">
                            <p-button
                                    label="Betrag überschreiben"
                                    icon="fa-regular fa-pen-to-square"
                                    severity="primary"
                                    [disabled]="!rowActionAvailable"
                                    [pTooltip]="amountOverwriteTooltip"
                                    tooltipPosition="top"
                                    (onClick)="openAmountOverwriteDialog()">
                            </p-button>

                            <p-button
                                    label="Berater wechseln"
                                    icon="fa-regular fa-arrow-right-arrow-left"
                                    severity="secondary"
                                    [outlined]="true"
                                    [disabled]="!rowActionAvailable"
                                    [pTooltip]="agentChangeTooltip"
                                    tooltipPosition="top"
                                    (onClick)="openAgentChangeDialog()">
                            </p-button>

                            <!-- Same place, same label — but it opens the create wizard against the
                                 Provisionszeile of the SELECTED row now (user decision). The old
                                 dialog cannot create a row; see the class TSDoc. It therefore needs
                                 a selection, which the old dialog did not: that one carried its own
                                 "Ereigniszeile" picker. -->
                            <p-button
                                    label="Satz manuell anlegen"
                                    icon="fa-regular fa-plus"
                                    severity="secondary"
                                    [outlined]="true"
                                    [disabled]="!manualRateAvailable"
                                    [pTooltip]="manualRateTooltip"
                                    tooltipPosition="top"
                                    (onClick)="openManualRateWizard()">
                            </p-button>

                            <p-divider layout="vertical" styleClass="h-2rem mx-1"></p-divider>

                            <p-button
                                    label="Provisionszeile anlegen"
                                    icon="fa-regular fa-file-circle-plus"
                                    severity="secondary"
                                    [outlined]="true"
                                    [disabled]="!canAgentChange"
                                    [pTooltip]="contractCreateTooltip"
                                    tooltipPosition="top"
                                    (onClick)="openContractCreateWizard()">
                            </p-button>

                            <p-button
                                    label="Provisionssatz anpassen"
                                    icon="fa-regular fa-sliders"
                                    severity="secondary"
                                    [outlined]="true"
                                    [disabled]="!rowActionAvailable"
                                    [pTooltip]="contractAdjustTooltip"
                                    tooltipPosition="top"
                                    (onClick)="openContractAdjustWizard()">
                            </p-button>
                        </div>
                    </div>

                    <!-- Selection line: which Satz the row actions write on and the detail pane shows -->
                    <div class="surface-card border-round p-3 mb-3">
                        <div class="flex flex-wrap align-items-center gap-3">
                            @if (selectedProvisionContractAgentId) {
                                <span class="text-color-secondary text-sm">Ausgewählter Provisionssatz</span>
                                <span class="font-medium">{{ selectedProvisionContractAgentLabel }}</span>

                                <p-button
                                        label="Auswahl aufheben"
                                        icon="fa-regular fa-xmark"
                                        severity="secondary"
                                        [text]="true"
                                        (onClick)="handleClearSelection()">
                                </p-button>
                            } @else if (allowDetailPane) {
                                <!-- "Provisionssatz wählen", not "Zeile wählen": since the create button
                                     is called "Provisionszeile anlegen", the word Zeile now names an
                                     ENTITY (cp.ProvisionContract) and no longer a table row. -->
                                <span class="text-color-secondary text-sm">
                                    Provisionssatz wählen, um ihn rechts zu öffnen, den Betrag zu
                                    überschreiben oder den Berater zu wechseln.
                                </span>
                            } @else {
                                <!-- List-only (side view): there is no right-hand pane to point at. -->
                                <span class="text-color-secondary text-sm">
                                    Provisionssatz wählen, um den Betrag zu überschreiben oder den Berater
                                    zu wechseln.
                                </span>
                            }
                        </div>
                    </div>

                    <!-- The actions are open by default (optimistic, see the class TSDoc), so this banner shows
                         only after the SERVER refused the right with a 403. The reason must be visible,
                         otherwise the disabled controls just look broken. -->
                    @if (!canAgentChange) {
                        <p-message severity="warn" styleClass="w-full mb-3">
                            <span>
                                Der Server hat die Änderung von Provisionssätzen verweigert: Ihnen fehlt das Recht
                                <strong>cp_agentChange</strong>. Die Liste bleibt lesbar.
                            </span>
                        </p-message>
                    }

                    <!-- Provisionssätze -->
                    @if (contractAgentsWidget) {
                        <mvs-widget #contractAgentsWidgetRef
                                [widgetData]="contractAgentsWidget"
                                (onObjectSelect)="handleContractAgentSelect($event)">

                            <!-- „Basis": the base the factor multiplies — monthly (Monatsbeiträge) or
                                 total-term (Promille), so Basis x Faktor = Betrag reads in one row. -->
                            <ng-template let-object="object" mvs-widget-custom-field-directive
                                         area="columns" [fieldId]="rateBasisFieldId" label="Basis">
                                @if (rateBasisOf(object); as basis) {
                                    @if (basis.value !== null) {
                                        <span [pTooltip]="basis.perMille ? 'Basis gesamt (Faktor in Promille)' : 'Basis monatlich (Faktor = Anzahl Monatsbeiträge)'">
                                            {{ basis.value | currency:'EUR':'symbol':'1.2-2' }}
                                        </span>
                                    } @else {
                                        <span class="text-color-secondary">–</span>
                                    }
                                }
                            </ng-template>

                            <!-- „Grund / Meldung": why a row pays nothing or an overwritten amount. -->
                            <ng-template let-object="object" mvs-widget-custom-field-directive
                                         area="columns" [fieldId]="rateReasonFieldId" label="Grund / Meldung">
                                @if (rateReasonOf(object); as reason) {
                                    <span class="cp-agent-rate-reason text-sm"
                                          [class.text-orange-700]="reason.severity === 'warn'"
                                          [class.text-blue-700]="reason.severity === 'info'"
                                          [class.text-color-secondary]="reason.severity === 'secondary'"
                                          [pTooltip]="reason.text">
                                        {{ reason.text }}
                                    </span>
                                }
                            </ng-template>

                        </mvs-widget>
                    }

                </div>
            </ng-template>

            <!-- RIGHT: the selected Provisionssatz in object mode. The pane stays mounted at all
                 times, only its CONTENT is conditional — so an <mvs-object> without an id never
                 occurs. The registry resolves 'cp.ProvisionContractAgent' to
                 CpProvisionContractAgentObjectComponent.
                 allowDetailPane is repeated here on purpose: [panes]="1" only COLLAPSES the pane by
                 CSS, it does not stop its content from being built. Without this guard the side view
                 would mount an <mvs-object> and fetch an entire OBC nobody can see. -->
            <ng-template #workbenchDetail>
                @if (allowDetailPane && selectedProvisionContractAgentId) {
                    <div class="cp-contract-agents-pane surface-card border-round p-3">
                        <mvs-object
                                [objectType]="'cp.ProvisionContractAgent'"
                                [objectId]="selectedProvisionContractAgentId">
                        </mvs-object>
                    </div>
                }
            </ng-template>

        </mvs-review-workbench>

        <!-- Action dialogs: metadata-driven forms, no hand-written form HTML -->
        <cp-provision-agent-amount-overwrite-dialog
                [provisionContractAgentId]="selectedProvisionContractAgentId"
                [provisionContractAgentLabel]="selectedProvisionContractAgentLabel"
                [canAgentChange]="canAgentChange"
                [(visible)]="showAmountOverwriteDialog"
                (onForbidden)="handleAgentChangeForbidden()"
                (onAmountOverwritten)="handleContractAgentChanged()">
        </cp-provision-agent-amount-overwrite-dialog>

        <cp-provision-agent-change-dialog
                [provisionContractAgentId]="selectedProvisionContractAgentId"
                [provisionContractAgentLabel]="selectedProvisionContractAgentLabel"
                [currentProvisionAgentId]="provisionAgentId"
                [currentProvisionAgentName]="provisionAgentName"
                [canAgentChange]="canAgentChange"
                [(visible)]="showAgentChangeDialog"
                (onForbidden)="handleAgentChangeForbidden()"
                (onAgentChanged)="handleContractAgentChanged()">
        </cp-provision-agent-change-dialog>

        <!-- The two top-down wizards. Same host contract as the dialogs above; started as a dialog,
             never through a route. "Satz manuell anlegen" now routes into the create wizard too —
             the manual-rate dialog component itself is untouched, only this call site moved. -->
        <!-- ONE instance for BOTH entries. [provisionContractId] is the whole difference:
             null  -> "Provisionszeile anlegen": the wizard creates the Zeile and its Sätze
             set   -> "Satz manuell anlegen": step 1 is a read-only context display, only Sätze
                      are captured. Both open methods set the field explicitly. -->
        <cp-provision-contract-create-wizard
                [provisionAgentId]="provisionAgentId"
                [provisionAgentName]="provisionAgentName"
                [provisionContractId]="createWizardProvisionContractId"
                [canAgentChange]="canAgentChange"
                [(visible)]="showContractCreateWizard"
                (onContractCreated)="handleWizardCompleted()"
                (onForbidden)="handleAgentChangeForbidden()">
        </cp-provision-contract-create-wizard>

        <cp-provision-contract-adjust-wizard
                [provisionContractId]="selectedProvisionContractId"
                [provisionContractAgentId]="selectedProvisionContractAgentId"
                [canAgentChange]="canAgentChange"
                [(visible)]="showContractAdjustWizard"
                (onAdjusted)="handleWizardCompleted()"
                (onForbidden)="handleAgentChangeForbidden()">
        </cp-provision-contract-adjust-wizard>

    }

</div>
